import React, { useEffect, useState } from "react";
import { L as Link, useLocaleNavigate } from "../lib/i18n";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useActiveLocale } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import CarCard from "../components/CarCard";
import RecentlyViewed from "../components/RecentlyViewed";
import TrendingNow from "../components/TrendingNow";
import GuestConversionHint from "../components/GuestConversionHint";
import { Search, ArrowRight, ShieldCheck, Car, BadgeCheck, Bolt, Zap, Crown, Mountain } from "lucide-react";
import JsonLd from "../components/JsonLd";
import { buildOrganizationSchema, buildWebsiteSchema } from "../lib/schema";
import { useSeoHead } from "../lib/seoHead";
import { getPageSeo, buildHreflangs, buildBreadcrumbList } from "../lib/pageSeo";

const SITE_URL = (process.env.REACT_APP_PUBLIC_URL || "").replace(/\/$/, "");

const HERO_BG = "https://images.unsplash.com/photo-1774177898002-5a6edaf5b73c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjYXIlMjBkcml2aW5nJTIwbW91bnRhaW4lMjByb2FkfGVufDB8fHx8MTc3ODA5MzY4MXww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { t } = useLang();
  const locale = useActiveLocale();
  const navigate = useLocaleNavigate();
  const seo = getPageSeo("home", locale);
  useSeoHead({
    title: seo.title,
    description: seo.description,
    canonical: `${SITE_URL}/${locale}`,
    image: `${SITE_URL}/icons/og-default.png`,
    hreflangs: buildHreflangs(SITE_URL, ""),
  });
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ total_listings: 0 });
  const [q, setQ] = useState("");

  useEffect(() => {
    api.listListings({ limit: 6, sort: "newest" }).then((d) => setFeatured(d.items)).catch(() => {});
    api.stats().then(setStats).catch(() => {});
  }, []);

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/listings?q=${encodeURIComponent(q)}`);
  };

  const categories = [
    { id: "ev", label: t.cat.ev, icon: Bolt, query: "?fuel=elektro", color: "from-emerald-500/20 to-emerald-500/0" },
    { id: "suv", label: t.cat.suv, icon: Mountain, query: "?body_type=suv", color: "from-amber-500/20 to-amber-500/0" },
    { id: "sport", label: t.cat.sport, icon: Zap, query: "?body_type=coupé", color: "from-red-500/20 to-red-500/0" },
    { id: "luxury", label: t.cat.luxury, icon: Crown, query: "?min_price=80000", color: "from-purple-500/20 to-purple-500/0" },
  ];

  return (
    <div>
      <JsonLd id="org" data={buildOrganizationSchema(SITE_URL)} />
      <JsonLd id="website" data={buildWebsiteSchema(SITE_URL)} />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--ch-bg-0)]/80 via-[var(--ch-bg-0)]/70 to-[var(--ch-bg-0)]" />
          <div className="absolute inset-0 bg-[radial-gradient(800px_400px_at_85%_30%,rgba(225,29,46,0.25),transparent_60%)]" />
        </div>
        <div className="relative mx-auto flex min-h-[82vh] max-w-7xl flex-col justify-center px-4 py-24 sm:px-6 lg:px-8">
          <p className="mb-6 inline-flex items-center gap-2 self-start rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-[0.25em] text-zinc-200 backdrop-blur">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--ch-red)]" /> {t.hero.tag}
          </p>
          <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl">
            {t.hero.headline}
          </h1>
          <p className="mt-6 max-w-2xl text-base text-zinc-300 sm:text-lg">{t.hero.sub}</p>

          {/* Premium glass search */}
          <form onSubmit={onSearch} className="glass mt-10 flex max-w-2xl flex-col gap-3 rounded-2xl p-3 sm:flex-row sm:items-center">
            <div className="flex flex-1 items-center gap-3 px-3">
              <Search className="h-5 w-5 text-zinc-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t.hero.search}
                data-testid="search-hero-input"
                className="border-0 bg-transparent text-base text-white placeholder:text-zinc-500 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>
            <Button type="submit" size="lg" data-testid="search-button" className="rounded-xl bg-[var(--ch-red)] text-white shadow-[0_8px_30px_rgba(225,29,46,0.35)] hover:bg-[var(--ch-red-soft)]">
              {t.hero.cta} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <div className="mt-12 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-6">
            <Stat icon={<Car className="h-4 w-4" />} value={stats.total_listings} label={t.nav.browse} />
            <Stat icon={<BadgeCheck className="h-4 w-4" />} value="100%" label={t.hero.tag} />
            <Stat icon={<ShieldCheck className="h-4 w-4" />} value="DE/FR/IT/EN" label="i18n" />
          </div>
        </div>
      </section>

      {/* Category tiles */}
      <section className="mx-auto max-w-7xl px-4 pt-20 sm:px-6 lg:px-8">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[var(--ch-red)]">Categories</p>
        <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">{t.hero.categories}</h2>
        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {categories.map((c) => {
            const Icon = c.icon;
            return (
              <Link
                key={c.id}
                to={`/listings${c.query}`}
                data-testid={`cat-${c.id}`}
                className={`group relative flex h-40 flex-col justify-end overflow-hidden rounded-2xl border border-[var(--ch-line)] bg-gradient-to-br ${c.color} bg-[var(--ch-bg-1)] p-5 transition-all hover:border-[var(--ch-red)]/40`}
              >
                <Icon className="absolute right-4 top-4 h-9 w-9 text-zinc-700 transition-transform duration-500 group-hover:scale-110 group-hover:text-zinc-300" />
                <p className="text-xs uppercase tracking-[0.25em] text-zinc-500">CarHive</p>
                <h3 className="mt-1 font-display text-2xl font-semibold text-white">{c.label}</h3>
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-zinc-400 group-hover:text-white">
                  Explore <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Recently Viewed — wiederkehrende User sehen ihre letzten Inserate direkt unter Hero */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <RecentlyViewed limit={6} title="Zuletzt angesehen" showViewAllLink={false} />
      </section>

      {/* Conversion-Hint für Gäste mit ≥3 Recent-Views (Phase 47.1) */}
      <GuestConversionHint />

      {/* Phase 62 — Trending Now (heat-score ranked rail) */}
      <TrendingNow locale={locale} limit={12} />

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[var(--ch-red)]">Featured</p>
            <h2 className="mt-2 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">{t.hero.featured}</h2>
          </div>
          <Link to="/listings" className="hidden items-center gap-2 text-sm text-zinc-400 hover:text-white sm:flex">
            {t.hero.browse_all} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {featured.map((car) => <CarCard key={car.id} car={car} />)}
          {featured.length === 0 && (
            <div className="col-span-full glass-soft rounded-xl p-12 text-center text-zinc-500">
              <Link to="/sell" className="text-[var(--ch-red-soft)] hover:underline">Erstes Inserat erstellen →</Link>
            </div>
          )}
        </div>
      </section>

      {/* Phase 66 — Dealer-Network section (Finanzierung removed; will return when bank partner integration ships) */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="ch-card relative overflow-hidden rounded-2xl p-8">
          <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-[var(--ch-navy)]/60 blur-3xl" />
          <BadgeCheck className="h-9 w-9 text-sky-400" />
          <h3 className="mt-5 font-display text-2xl font-semibold text-white">{t.hero.dealers}</h3>
          <p className="mt-3 max-w-md text-sm text-zinc-400">{t.hero.dealersCopy}</p>
          <Button asChild variant="outline" className="mt-6 border-white/10 bg-white/5 text-white hover:bg-white/10">
            <Link to="/listings?seller_type=dealer">→ Pro</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon, value, label }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500">{icon} {label}</div>
      <p className="mt-2 font-display text-2xl font-bold text-white">{value}</p>
    </div>
  );
}
